import React, { useEffect, useState, useMemo } from 'react'
import { Button, Card, Form, Input, InputNumber, Modal, Select, Space, Spin, message } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import docsService from '../services/docsService'

import { adminPath } from '../../shared/config/paths';

const MarkdownEditor = ({ value, onChange }) => (
  <Editor
    height="500px"
    defaultLanguage="markdown"
    theme="vs-dark"
    value={value || ''}
    onChange={(val) => onChange?.(val)}
    options={{
      minimap: { enabled: false },
      wordWrap: 'on',
      lineNumbers: 'on',
      fontSize: 14,
      scrollBeyondLastLine: false
    }}
  />
)

const locales = [
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ms', label: 'Bahasa Melayu' }
]

const DocsEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pages, setPages] = useState([])
  const isEditing = !!id

  const currentLocale = Form.useWatch('locale', form) || 'en'

  const filteredPages = useMemo(
    () => pages.filter((p) => p.locale === currentLocale && p.id !== Number(id)),
    [pages, currentLocale, id]
  )

  useEffect(() => {
    loadPages()
    if (isEditing) {
      loadDoc()
    } else {
      form.setFieldsValue({
        locale: 'en',
        status: 'draft',
        type: 'page',
        display_order: 0,
        meta: {}
      })
    }
  }, [id])

  const loadPages = async () => {
    try {
      const res = await docsService.list({})
      const items = res.data?.items || res.items || res.data || []
      setPages(Array.isArray(items) ? items : [])
    } catch (err) {
      console.error('加载文档列表失败', err)
    }
  }

  const loadDoc = async () => {
    setLoading(true)
    try {
      const res = await docsService.get(id)
      const doc = res.data || res
      form.setFieldsValue({
        ...doc,
        meta: doc.meta || {},
        parent_id: doc.parent_id || null
      })
    } catch (err) {
      message.error(err?.message || '加载文档失败')
      navigate(adminPath('/docs'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    try {
      setSaving(true)
      const payload = await form.validateFields()
      let saved
      if (isEditing) {
        payload.id = Number(id)
        saved = await docsService.update(payload)
        message.success('已更新')
      } else {
        saved = await docsService.create(payload)
        message.success('已创建')
      }
      // 非中文文档里残留中文控制台文案：已保存，但留在编辑页提示改成该语言的控制台译文
      const warnings = saved?.label_warnings || saved?.data?.label_warnings
      if (Array.isArray(warnings) && warnings.length) {
        const total = saved?.label_warning_count || saved?.data?.label_warning_count || warnings.length
        Modal.warning({
          title: `文档里有 ${total} 处中文界面文案`,
          width: 560,
          content: (
            <div style={{ maxHeight: 320, overflow: 'auto' }}>
              <div style={{ marginBottom: 8 }}>控制台已多语言，请写成该语言下控制台显示的文字。</div>
              {warnings.slice(0, 20).map((w, i) => (
                <div key={i}>{w.field}:{w.line} 「{w.text}」{w.suggest ? ` → ${w.suggest}` : ''}</div>
              ))}
            </div>
          )
        })
        const savedId = saved?.id || saved?.data?.id
        if (!isEditing && savedId) navigate(adminPath(`/docs/${savedId}/edit`))
        return
      }
      navigate(adminPath('/docs'))
    } catch (err) {
      if (err?.errorFields) return
      message.error(err?.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    navigate(adminPath('/docs'))
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            返回
          </Button>
          <span>{isEditing ? '编辑文档' : '新建文档'}</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="页面标题" />
          </Form.Item>
          <Form.Item name="slug" label="Slug" rules={[{ required: true, message: '请输入 slug' }]}>
            <Input placeholder="examples: '', getting-started, api/auth" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="locale" label="Locale" rules={[{ required: true }]}>
            <Select options={locales.map((l) => ({ label: l.label, value: l.code }))} />
          </Form.Item>
          <Form.Item name="parent_id" label="父节点">
            <Select
              allowClear
              placeholder="可选"
              options={filteredPages.map((p) => ({ label: `${p.title} (${p.slug || 'root'})`, value: p.id }))}
            />
          </Form.Item>
          <Form.Item name="type" label="类型">
            <Select
              options={[
                { value: 'page', label: '页面' },
                { value: 'section', label: 'Section' }
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'published', label: '已发布' }
              ]}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="display_order" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="product" label="产品标签">
            <Input placeholder="可选：如 CDN, Host, DNS" />
          </Form.Item>
          <Form.Item name="category" label="分类">
            <Input placeholder="可选：如 operations, api, guide" />
          </Form.Item>
          <Form.Item name="reading_time" label="阅读时间（分钟）">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Form.Item name="summary" label="摘要">
          <Input.TextArea rows={2} placeholder="简短描述" />
        </Form.Item>

        <Form.Item name="content" label="内容 (Markdown)">
          <MarkdownEditor />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name={['meta', 'hero_title']} label="Hero 标题">
            <Input placeholder="单独的 hero 标题（可选）" />
          </Form.Item>
          <Form.Item name={['meta', 'hero_subtitle']} label="Hero 副标题">
            <Input placeholder="Hero 副标题（可选）" />
          </Form.Item>
          <Form.Item name={['meta', 'badge']} label="Badge">
            <Input placeholder="例如：API, Playbook" />
          </Form.Item>
          <Form.Item name="version" label="版本">
            <Input placeholder="如 v1.0" />
          </Form.Item>
        </div>
      </Form>
    </Card>
  )
}

export default DocsEditor
